import React, { useState, useEffect } from 'react';
import { getBackendUrl } from '@/config';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Breadcrumbs } from '@/components/shared/Breadcrumbs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, User, FileText, Building2, Mail, Phone, MapPin, Calendar } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

export default function ClientFilingDetailView() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && clientId) {
      fetchClientDetail();
    }
  }, [user, clientId]);

  const fetchClientDetail = async () => {
    try {
      setLoading(true);
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      
      const response = await axios.get(`${backendUrl}/api/agent/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setClient(response.data);
    } catch (error) {
      console.error('Error fetching client detail:', error);
      toast.error('Failed to load client details');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return <div>Loading...</div>;

  if (loading) {
    return (
      <DashboardLayout role={user.role} title="Client Details" user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!client) {
    return (
      <DashboardLayout role={user.role} title="Client Details" user={user}>
        <div className="text-center py-12">
          <User className="h-16 w-16 mx-auto text-muted-foreground" />
          <h3 className="mt-4 text-lg font-semibold">Client not found</h3>
          <Button className="mt-4" onClick={() => navigate('/agent/clients')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Clients
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'submitted':
      case 'approved':
        return 'default';
      case 'in_review':
        return 'secondary';
      case 'draft':
        return 'outline';
      default:
        return 'default';
    }
  };

  return (
    <DashboardLayout role={user.role} title="Client Details" user={user}>
      <div className="space-y-6">
        <Breadcrumbs items={[
          { label: 'Agent', href: '/agent/dashboard' },
          { label: 'Clients', href: '/agent/clients' },
          { label: 'Client Details' }
        ]} />

        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Client Details</h2>
            <p className="text-muted-foreground">Complete client information</p>
          </div>
          <Button variant="outline" onClick={() => navigate('/agent/clients')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Clients
          </Button>
        </div>

        {/* Client Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Client Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <p className="text-sm text-muted-foreground">Full Name</p>
                <p className="font-medium">{`${client.first_name || ''} ${client.last_name || ''}`.trim() || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Role</p>
                <Badge variant="outline" className="capitalize">{client.role}</Badge>
              </div>
            </div>
            
            <div className="grid gap-4 md:grid-cols-2">
              <div className="flex items-start gap-2">
                <Mail className="h-4 w-4 text-muted-foreground mt-1" />
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{user?.role === 'admin' ? (client.email || 'N/A') : '••••••@••••.com'}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Phone className="h-4 w-4 text-muted-foreground mt-1" />
                <div>
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p className="font-medium">{user?.role === 'admin' ? (client.phone || client.phone_number || 'N/A') : '•••-•••-••••'}</p>
                </div>
              </div>
            </div>

            {client.address && (
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground mt-1" />
                <div>
                  <p className="text-sm text-muted-foreground">Address</p>
                  <p className="font-medium">
                    {client.address}<br />
                    {client.city && client.state && client.zip && (
                      `${client.city}, ${client.state} ${client.zip}`
                    )}
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-start gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground mt-1" />
              <div>
                <p className="text-sm text-muted-foreground">Member Since</p>
                <p className="font-medium">{client.created_at ? new Date(client.created_at).toLocaleDateString() : 'N/A'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tax Filings */}
        {client.tax_filings && client.tax_filings.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Tax Filings ({client.tax_filings.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {client.tax_filings.map((filing) => (
                  <div key={filing.id} className="p-4 border rounded-lg hover:border-primary transition-colors">
                    <div className="grid gap-2 md:grid-cols-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Tax Year</p>
                        <p className="font-medium">{filing.tax_year}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Type</p>
                        <p className="font-medium capitalize">{filing.filing_type || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Status</p>
                        <Badge variant={getStatusBadgeVariant(filing.status)}>{filing.status}</Badge>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Updated</p>
                        <p className="font-medium">{filing.updated_at ? new Date(filing.updated_at).toLocaleDateString() : 'N/A'}</p>
                      </div>
                    </div>
                    <div className="mt-2">
                      <Button 
                        size="sm" 
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        onClick={() => navigate(`/agent/filings/${filing.id}`, { state: { clientId } })}
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* LLC Formations */}
        {client.llc_formations && client.llc_formations.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                LLC Formations ({client.llc_formations.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {client.llc_formations.map((llc) => (
                  <div key={llc.id} className="p-4 border rounded-lg hover:border-primary transition-colors">
                    <div className="grid gap-2 md:grid-cols-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Company Name</p>
                        <p className="font-medium">{llc.company_name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">State</p>
                        <p className="font-medium">{llc.state}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Status</p>
                        <Badge variant={getStatusBadgeVariant(llc.status)}>{llc.status}</Badge>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Updated</p>
                        <p className="font-medium">{llc.updated_at ? new Date(llc.updated_at).toLocaleDateString() : 'N/A'}</p>
                      </div>
                    </div>
                    <div className="mt-2">
                      <Button 
                        size="sm" 
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        onClick={() => navigate(`/agent/llc/${llc.id}`, { state: { clientId } })}
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* No Activity */}
        {(!client.tax_filings || client.tax_filings.length === 0) && 
         (!client.llc_formations || client.llc_formations.length === 0) && (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No filings or LLC formations yet</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
